import { take } from 'rxjs/operators';
import { inject, Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { NavigationStart, RouterEvent, Router, NavigationEnd, Event } from '@angular/router';
import { AuthService } from './auth.service';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';
import {
  Analytics,
  isSupported,
  logEvent as firebaseLogEvent,
  setAnalyticsCollectionEnabled,
  setUserId
} from '@angular/fire/analytics';
import { DbService } from './db.service';

declare let newrelic;

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private _db = inject(DbService);
  private _injector = inject(Injector);

  // Resolved lazily: injecting `Analytics` directly in DI throws if AngularFire's
  // async `isSupported()` probe hasn't settled yet (the "APP_INITIALIZER ... has not
  // resolved" error). Per AngularFire's guidance we await isSupported() first, then
  // pull the instance from the injector. Null on the server / unsupported browsers.
  private _analytics: Analytics | null = null;
  private _analyticsReady: Promise<Analytics | null>;

  private _navStartUrl = '';
  private _navEndUrl = '';
  isBrowser = false;
  constructor(
    private authService: AuthService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this._analyticsReady = this.resolveAnalytics();
    this.router.events.subscribe(
      (e: Event | RouterEvent) => {
        if (this.isBrowser) {
          if (e instanceof NavigationStart) {
            this._navStartUrl = e.url;
            this.logEvent(
              'screen_view',
              {
                firebase_screen: e.url
              },
              false
            );
            this.logEvent('page_view', { page: e.url }, false);
            this.newRelicInteraction(e.url);
          }
          if (e instanceof NavigationEnd && this._navStartUrl !== e.url) {
            this._navEndUrl = e.url;
            this.logEvent(
              'screen_view',
              {
                firebase_screen: e.url
              },
              false
            );
            this.logEvent('page_view', { page: e.url }, false);
            this.newRelicInteraction(e.url);
          }
        }
      },
      (error) => {
        console.error(error);
      }
    );
  }

  private async resolveAnalytics(): Promise<Analytics | null> {
    if (!this.isBrowser) {
      return null;
    }
    try {
      if (!(await isSupported())) {
        return null;
      }
      this._analytics = this._injector.get(Analytics);
      return this._analytics;
    } catch (error) {
      console.error('Analytics unavailable: ', error);
      return null;
    }
  }

  async enable(): Promise<void> {
    const analytics = await this._analyticsReady;
    if (analytics?.app) {
      this.setUser('VISITOR');
      setAnalyticsCollectionEnabled(analytics, true);
    }
    return;
  }

  async setUser(uid: string) {
    const analytics = await this._analyticsReady;
    if (uid && analytics?.app) {
      setUserId(analytics, uid);
    }
  }

  getNavUrls() {
    return { start: this._navStartUrl, end: this._navEndUrl };
  }

  newRelicInteraction(name: string) {
    if (this.isBrowser && newrelic) {
      newrelic.setPageViewName(name, environment.production ? 'russellsny.com' : 'development');
      newrelic.setCurrentRouteName(name);
      let i = newrelic.interaction();
      i.setName(name);
      i.save();
    }
  }

  async logEvent(eventName: string, data: any = {}, persistToDb = true): Promise<void> {
    const analytics = await this._analyticsReady;
    if (analytics?.app) {
      firebaseLogEvent(analytics, eventName, data);
    }
    const user = await this.authService.getCurrentUser();
    const isWholesaleSegment = await this.authService.userHasRole('wholesale');

    if (persistToDb) {
      const id = this._db.createId('user-analytic-events');
      const event: any = {
        collection: 'user-analytic-events',
        eventName,
        data,
        auth: user?.uid || 'retail',
        name: user?.displayName || 'retail',
        isWholesaleSegment,
        isLoggedIn: user?.uid ? true : false,
        id: id.id,
        createdAt: new Date()
      } as any;
      await this._db.setDoc('user-analytic-events', id.id, event);
    }
    return;
  }
}
